import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Button as MuiButton,
  Container,
  Link,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DataTable, { TableColumn } from "react-data-table-component";

import { history } from "../../..";
import PageHero from "../../shared/page-hero";
import Button from "../../shared/Button";
import { tableCustomStyles } from "../../../utils/table-styles";
import storageAreaService from "../../../services/storageAreaService";
import { ContainmentPad } from "../../../interface/api/ContainmentPad";
import { ContainmentPadSample } from "../../../interface/api/ContainmentPadSample";
import { HydrovacTicket } from "../../../interface/api/HydrovacTicket";
import { formatTicketNumber } from "../../../utils/parser";
import { formatDateTime } from "../../../utils/date-helper";
import ContainmentPadApprovalDialog from "./containment-pad-approval-dialog";
import PaginatedTableTop, { paginationTopClassName } from "../../shared/paginated-table-top";

const useStyles = makeStyles({
  section: { marginTop: "2rem", maxWidth: 480 },
});

const isImageLab = (key?: string | null) =>
  !!key && /\.(png|jpe?g|gif|webp|bmp)$/i.test(key);

const ContainmentPadSamplingView = () => {
  const classes = useStyles();
  const { id }: any = useParams();
  const padId = Number(id);

  const [pad, setPad] = useState<ContainmentPad | null>(null);
  const [loads, setLoads] = useState<HydrovacTicket[]>([]);
  const [sample, setSample] = useState<ContainmentPadSample | null>(null);
  const [approvalOpen, setApprovalOpen] = useState(false);

  const loadData = () => {
    storageAreaService.getContainmentPad(padId).then((r) => {
      if (r.ok) setPad(r.data as ContainmentPad);
    });
    storageAreaService.getContainmentPadLoads(padId).then((r) => {
      if (r.ok) setLoads(r.data as HydrovacTicket[]);
    });
    storageAreaService.getContainmentPadSample(padId).then((r) => {
      if (r.ok) setSample(r.data as ContainmentPadSample);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const status = pad?.status ?? "";
  const isToBeSampled = status === "To Be Sampled";
  const isClosed = status === "Closed";
  const hasLab = !!pad?.labResultKey;
  const pendingHeadspaceCount = loads.filter((l) => !l.isComplete).length;
  const allHeadspaceDone = loads.length > 0 && pendingHeadspaceCount === 0;
  const canClose = hasLab && allHeadspaceDone;

  const onUpload = (file: File | null) => {
    if (!file) return;
    storageAreaService.uploadContainmentPadLab(padId, file).then((r) => {
      if (r.ok) {
        toast.success("Lab result uploaded");
        loadData();
      } else {
        toast.error((r.data as string) || "Upload failed");
      }
    });
  };

  const onRemoveLab = () => {
    storageAreaService.removeContainmentPadLab(padId).then((r) => {
      if (r.ok) {
        toast.success("Lab result removed");
        loadData();
      } else {
        toast.error((r.data as string) || "Failed to remove lab result");
      }
    });
  };

  const onApprove = (
    sampleDate: string,
    sampleReferenceNumber: string,
    signature: string
  ) => {
    storageAreaService
      .approveContainmentPad(padId, sampleDate, sampleReferenceNumber, signature)
      .then((r) => {
        if (r.ok) {
          toast.success("Containment pad approved and closed");
          history.push("/storage-areas");
        } else {
          toast.error((r.data as string) || "Failed to approve");
        }
      });
  };

  const columns: TableColumn<HydrovacTicket>[] = [
    {
      name: "Ticket #",
      selector: (row) => row.ticketNumber,
      sortable: true,
      cell: (row) => formatTicketNumber(row.ticketNumber),
    },
    {
      name: "Contractor",
      selector: (row) => row.contractorCompanyName ?? "",
    },
    {
      name: "Property Address",
      selector: (row) => row.propertyAddress ?? "",
    },
    {
      name: "Received",
      selector: (row) => (row.timeUnloaded as any) ?? "",
      cell: (row) => formatDateTime(row.timeUnloaded as any, false),
    },
    {
      name: "Shipping Company",
      selector: (row) => row.shippingCompany ?? "",
    },
    {
      name: "Headspace",
      selector: (row) => row.headspaceReading ?? "",
    },
    {
      name: "Status",
      selector: (row) =>
        row.isComplete
          ? "Checked"
          : row.isPendingHeadspace
          ? "Pending Headspace"
          : "—",
    },
  ];

  return (
    <Container maxWidth="xl">
      <PageHero
        title={pad ? pad.name ?? "Containment Pad" : "Containment Pad"}
        subtitle={
          isToBeSampled
            ? "Upload the single mixed-sample lab result, then sign to approve & close."
            : isClosed
            ? `Closed${
                pad?.approvalDate
                  ? ` — approved ${formatDateTime(pad.approvalDate, true)}`
                  : ""
              }.`
            : `${loads.length} load(s) assigned · Status: ${status || "—"}.`
        }
        showBackButton={true}
      />

      {(isToBeSampled || isClosed) && (
        <Box className={classes.section} style={{ marginTop: "1rem" }}>
          <Typography variant="h6" gutterBottom>
            Sample &amp; Lab Result
          </Typography>
          {sample?.sampleReferenceNumber && (
            <Typography variant="body2" gutterBottom>
              Sample Reference #: {sample.sampleReferenceNumber}
            </Typography>
          )}
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {hasLab ? (
              sample?.labResultUrl && isImageLab(pad?.labResultKey) ? (
                <a
                  href={sample.labResultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={sample.labResultUrl}
                    alt="Lab result"
                    style={{
                      height: 60,
                      width: "auto",
                      borderRadius: 4,
                      border: "1px solid rgba(0,0,0,0.15)",
                    }}
                  />
                </a>
              ) : sample?.labResultUrl ? (
                <Link
                  href={sample.labResultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View lab result
                </Link>
              ) : (
                <Typography variant="body2" color="primary">
                  Uploaded
                </Typography>
              )
            ) : (
              isToBeSampled && (
                <MuiButton variant="outlined" color="primary" size="small" component="label">
                  Upload Lab Result
                  <input
                    hidden
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => onUpload(e.target.files?.[0] ?? null)}
                  />
                </MuiButton>
              )
            )}
            {isToBeSampled && hasLab && (
              <MuiButton size="small" style={{ color: "#c62828" }} onClick={onRemoveLab}>
                Remove
              </MuiButton>
            )}
          </Box>

          {isToBeSampled && (
            <Box mt={2}>
              <Button
                label="Sign & Close"
                color="primary"
                variant="contained"
                type="button"
                onClick={() => setApprovalOpen(true)}
                disabled={!canClose}
              />
              {!allHeadspaceDone && (
                <Typography
                  variant="caption"
                  color="error"
                  style={{ display: "block", marginTop: 4 }}
                >
                  {pendingHeadspaceCount} load(s) still need a headspace check
                  before this pad can be closed.
                </Typography>
              )}
              {allHeadspaceDone && !hasLab && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginTop: 4 }}
                >
                  Upload the lab result to enable closing.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Typography variant="h6" style={{ marginTop: "1rem" }}>
        Loads ({loads.length})
      </Typography>
      <PaginatedTableTop>
        <DataTable
          columns={columns}
          data={loads ?? []}
          customStyles={tableCustomStyles}
          className={paginationTopClassName}
          striped
          highlightOnHover
          responsive
          pagination
          paginationPerPage={25}
          paginationRowsPerPageOptions={[25, 50, 100, 200]}
        />
      </PaginatedTableTop>

      <ContainmentPadApprovalDialog
        open={approvalOpen}
        padName={pad?.name ?? null}
        sampleReferenceNumber={
          pad?.sampleTakenReferenceNumber ||
          (pad?.sequenceNumber != null ? `CP${pad.sequenceNumber}` : "")
        }
        onCancel={() => setApprovalOpen(false)}
        onApprove={(sampleDate, sampleReferenceNumber, signature) => {
          setApprovalOpen(false);
          onApprove(sampleDate, sampleReferenceNumber, signature);
        }}
      />
    </Container>
  );
};

export default ContainmentPadSamplingView;
